import { useTranslation } from 'react-i18next';
import { ChevronRight, Package, Store, User } from 'lucide-react';
import { Link } from 'react-router';
import { formatDate, formatOrderTotal } from '~/utils';
import { OrderStatusBadge } from '~/components/ui/OrderStatusBadge';
import { Button } from '~/components/ui';
import { OrderCardDetails } from '~/pages/orders/sections/OrderCardDetails';
import {
  NO_PRODUCTS,
  type OrderCardProduct,
  type OrderCardPlant,
} from '~/pages/orders/lib/orderCardProducts';

export function OrderCard({
  id,
  status,
  orderDate,
  sellingOrganizationName,
  itemCount,
  totalAmount,
  currency,
  parentOrder,
  products = NO_PRODUCTS,
  plant,
  deliveryLocation,
  createdBy,
  onClick,
  onCopyToCart,
  isCopyingToCart = false,
  copyToCartDisabled = false,
  copyToCartDisabledReason,
}: {
  id: string;
  status: string;
  orderDate: string;
  sellingOrganizationName: string;
  itemCount: number;
  totalAmount: number | null;
  currency?: string;
  parentOrder?: { id: string } | null;
  products?: OrderCardProduct[];
  plant?: OrderCardPlant | null;
  deliveryLocation?: string | null;
  createdBy?: { name: string; email?: string | null } | null;
  onClick?: () => void;
  onCopyToCart?: () => void;
  isCopyingToCart?: boolean;
  copyToCartDisabled?: boolean;
  copyToCartDisabledReason?: string;
}) {
  const { t } = useTranslation('order');
  const copyButton = (
    <Button
      size="sm"
      variant="accent"
      onClick={onCopyToCart}
      loading={isCopyingToCart}
      disabled={copyToCartDisabled}
      width="8rem"
      data-testid="order-card-copy-to-cart"
    >
      {t('order.copy_to_cart_button')}
    </Button>
  );
  return (
    <div
      data-testid="order-card"
      className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="p-4 sm:p-6 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3
                className="text-neut-prim font-semibold"
                data-testid="order-card-id"
              >
                {id}
              </h3>
              <OrderStatusBadge status={status} />
              {parentOrder && (
                <Link
                  to={`/orders/view/${parentOrder.id}`}
                  onClick={(event) => event.stopPropagation()}
                  className="bg-indigo-50 text-indigo-600 rounded-full px-2 py-1 text-xs font-medium hover:bg-indigo-100 transition-colors"
                >
                  {t('order.child_of_parent', {
                    parentOrderId: parentOrder.id.slice(0, 8),
                  })}
                </Link>
              )}
            </div>

            <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
              <span>{formatDate(orderDate)}</span>
              {createdBy && (
                <>
                  <span>•</span>
                  <div
                    className="flex items-center gap-2"
                    data-testid="order-created-by"
                  >
                    <User size={14} />
                    <span>
                      {createdBy.name} ({createdBy.email})
                    </span>
                  </div>
                </>
              )}
              <span>•</span>
              <div className="flex items-center gap-2">
                <Store size={14} />
                <span>{sellingOrganizationName}</span>
              </div>
            </div>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-xs text-gray-500 mb-1">
              {t('order.total_amount')}
            </p>
            <p className="text-lg text-neut-prim font-semibold">
              {formatOrderTotal(
                totalAmount,
                t('order.not_available'),
                currency
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <OrderCardDetails
          products={products}
          plant={plant}
          deliveryLocation={deliveryLocation}
        />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Package size={16} />
            <span>{t('order.item_count', { count: itemCount })}</span>
          </div>
          <div className="flex items-center">
            {onCopyToCart &&
              (copyToCartDisabledReason ? (
                <span title={copyToCartDisabledReason} className="inline-flex">
                  {copyButton}
                </span>
              ) : (
                copyButton
              ))}
            <Button variant="ghost-primary" onClick={onClick} width="10rem">
              {t('order.view_details')}
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
